import { Link, Navigate, useLocation } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { getAuthorityService } from "@/data/authorityServices";
import { getExpansionTranslation, TRANSLATED_SERVICE_SLUGS } from "@/data/expansionServices";
import { detectLocale, stripLocale, SUPPORTED_LOCALES, DEFAULT_LOCALE, t as tr } from "@/lib/i18n";

/**
 * Shared template for the Sprint F authority-service pages
 * (/services/furniture-sourcing-company-india etc.).
 *
 * Each page is data-driven from authorityServices.js — the template
 * emits the full SEO stack (Service + FAQPage + BreadcrumbList schema),
 * a lede, ordered body sections, an internal-link grid and CTAs.
 */
const AuthorityService = () => {
  const location = useLocation();
  const locale = detectLocale(location.pathname);
  const canonicalPath = stripLocale(location.pathname);
  // Accept both /services/<slug> and /industries/<slug> — the industry
  // page `/industries/home-lifestyle-sourcing-india` reuses this template.
  const slug = canonicalPath
    .replace(/^\/services\//, "")
    .replace(/^\/industries\//, "")
    .replace(/\/$/, "");
  const enSvc = getAuthorityService(slug);
  const translation = locale !== DEFAULT_LOCALE ? getExpansionTranslation(locale, slug) : null;
  // Merge translation on top of the English base so partially-translated
  // pages still render — missing fields fall back to English.
  const svc = translation ? { ...enSvc, ...translation } : enSvc;
  const { open: openProject } = useStartProject();
  const routePrefix = (enSvc && enSvc.routePrefix) || "/services";
  const localePrefix = SUPPORTED_LOCALES.find((l) => l.code === locale)?.pathPrefix || "";
  const enRoutePath = enSvc ? `${routePrefix}/${enSvc.slug}` : location.pathname;
  // Untranslated-locale fallback (e.g. /it/services/…): render the English
  // master content but canonicalise + schema-link to the English URL so the
  // fallback never self-canonicalises duplicate content.
  const isUntranslatedLocale = locale !== DEFAULT_LOCALE && !translation;
  const routePath = enSvc
    ? isUntranslatedLocale
      ? enRoutePath
      : `${localePrefix}${routePrefix}/${enSvc.slug}`
    : location.pathname;
  // Hreflang alternates — this page + every translated equivalent + x-default.
  const alternates = enSvc
    ? [
        { hreflang: "en", href: `${SITE_URL}${enRoutePath}` },
        ...Object.entries(TRANSLATED_SERVICE_SLUGS)
          .filter(([, slugs]) => slugs.includes(enSvc.slug))
          .map(([lc]) => {
            const pre = SUPPORTED_LOCALES.find((l) => l.code === lc)?.pathPrefix || "";
            return { hreflang: lc, href: `${SITE_URL}${pre}${routePrefix}/${enSvc.slug}` };
          }),
        { hreflang: "x-default", href: `${SITE_URL}${enRoutePath}` },
      ]
    : [];

  useSeo({
    title: svc ? svc.title : "Service — Asia Sourcing",
    description: svc ? svc.metaDescription : "",
    path: svc ? routePath : "/services",
    canonical: svc ? `${SITE_URL}${routePath}` : null,
    alternates: alternates.length ? alternates : null,
    type: "article",
    jsonLd: svc && {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${SITE_URL}${routePath}#service`,
          name: svc.primaryKeyword,
          description: svc.metaDescription,
          serviceType: svc.primaryKeyword,
          areaServed: [
            { "@type": "Country", name: "United Kingdom" },
            { "@type": "Country", name: "United States" },
            { "@type": "Country", name: "Germany" },
            { "@type": "Country", name: "France" },
            { "@type": "Country", name: "Netherlands" },
            { "@type": "Country", name: "Australia" },
          ],
          provider: {
            "@type": "Organization",
            "@id": `${SITE_URL}#organization`,
            name: "Asia Sourcing India",
            url: SITE_URL,
          },
          url: `${SITE_URL}${routePath}`,
        },
        {
          "@type": "FAQPage",
          mainEntity: svc.faqs.map(([question, answer]) => ({
            "@type": "Question",
            name: question,
            acceptedAnswer: { "@type": "Answer", text: answer },
          })),
        },
        breadcrumb([
          ["Home", SITE_URL],
          [routePrefix === "/industries" ? "Industries" : "Services", `${SITE_URL}${routePrefix}`],
          [svc.primaryKeyword, `${SITE_URL}${routePath}`],
        ]),
      ],
    },
  });

  if (!svc) return <Navigate to="/services" replace />;

  return (
    <article data-testid={`authority-service-${svc.slug}`}>
      <section className="wrap" style={{ paddingBlock: "clamp(3rem, 5vw, 5rem)" }}>
        <Reveal as="p" className="eyebrow">{svc.eyebrow}</Reveal>
        <WordReveal
          as="h1"
          className="display"
          style={{
            fontSize: "clamp(1.9rem, 3.6vw, 3.2rem)",
            margin: "0 0 1.2rem",
            letterSpacing: "-.015em",
            lineHeight: 1.08,
          }}
          text={svc.h1}
        />
        <Reveal
          as="p"
          style={{
            color: "var(--ink-soft)",
            fontSize: "1.08rem",
            lineHeight: 1.75,
            maxWidth: "72ch",
            margin: "0 0 2rem",
          }}
        >
          {svc.lede}
        </Reveal>
        <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={openProject}
            className="btn btn-primary"
            data-testid={`authority-service-${svc.slug}-cta-primary`}
          >
            Send your requirement
          </button>
          <Link
            to="/contact"
            className="btn btn-ghost"
            data-testid={`authority-service-${svc.slug}-cta-secondary`}
          >
            Talk to our sourcing team →
          </Link>
        </div>
      </section>

      <section className="wrap" style={{ paddingBlock: "clamp(1rem, 2vw, 2rem)" }}>
        {svc.sections.map((s, i) => (
          <div
            key={i}
            style={{
              borderTop: "1px solid var(--line)",
              paddingBlock: "clamp(1.6rem, 3vw, 2.4rem)",
            }}
          >
            <Reveal
              as="h2"
              className="display"
              style={{
                fontSize: "clamp(1.4rem, 2.2vw, 1.85rem)",
                margin: "0 0 1rem",
                letterSpacing: "-.01em",
              }}
            >
              {s.h2}
            </Reveal>
            {s.body.map((p, j) => (
              <Reveal
                as="p"
                key={j}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.02rem",
                  lineHeight: 1.75,
                  maxWidth: "72ch",
                  margin: "0 0 1rem",
                }}
              >
                {p}
              </Reveal>
            ))}
          </div>
        ))}
      </section>

      <section
        className="wrap"
        style={{ paddingBlock: "clamp(2.4rem, 5vw, 4rem)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
          Related India sourcing capabilities
        </Reveal>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: ".8rem",
          }}
        >
          {svc.relatedInternal.map((r, i) => (
            <Link
              key={i}
              to={r.to}
              className="btn btn-ghost"
              data-testid={`authority-service-${svc.slug}-related-${i}`}
              style={{ justifyContent: "flex-start" }}
            >
              {r.name} →
            </Link>
          ))}
        </div>
      </section>

      <section
        className="wrap"
        style={{ paddingBlock: "clamp(2.4rem, 5vw, 4rem)", borderTop: "1px solid var(--line)" }}
      >
        <Reveal as="p" className="eyebrow" style={{ marginBottom: ".8rem" }}>
          {svc.primaryKeyword} · Frequently asked questions
        </Reveal>
        <div style={{ maxWidth: "76ch" }}>
          {svc.faqs.map(([q, a], i) => (
            <details
              key={i}
              data-testid={`authority-service-${svc.slug}-faq-${i}`}
              style={{ borderTop: "1px solid var(--line)", padding: "1.1rem 0" }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontFamily: "var(--display)",
                  fontSize: "1.08rem",
                  lineHeight: 1.4,
                  color: "var(--ink)",
                }}
              >
                {q}
              </summary>
              <p
                style={{
                  marginTop: ".8rem",
                  color: "var(--ink-soft)",
                  fontSize: "1rem",
                  lineHeight: 1.75,
                }}
              >
                {a}
              </p>
            </details>
          ))}
        </div>
      </section>

      <section
        className="wrap"
        style={{ paddingBlock: "clamp(3rem, 5vw, 5rem)", borderTop: "1px solid var(--line)" }}
      >
        <WordReveal
          as="h2"
          className="display"
          style={{ fontSize: "clamp(1.6rem, 2.8vw, 2.2rem)", margin: "0 0 1rem", letterSpacing: "-.01em", lineHeight: 1.12 }}
          text={`Start your ${svc.primaryKeyword.toLowerCase()} engagement.`}
        />
        <Reveal
          as="p"
          style={{ color: "var(--ink-soft)", fontSize: "1.02rem", lineHeight: 1.7, maxWidth: "68ch", margin: "0 0 1.4rem" }}
        >
          Share a written brief and we return a three-to-five factory shortlist within 5-7 working days.
        </Reveal>
        <div style={{ display: "flex", gap: ".8rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={openProject}
            className="btn btn-primary"
            data-testid={`authority-service-${svc.slug}-cta-final`}
          >
            Request a sourcing consultation
          </button>
          <Link
            to="/contact"
            className="btn btn-ghost"
            data-testid={`authority-service-${svc.slug}-cta-final-secondary`}
          >
            Contact us →
          </Link>
        </div>
      </section>
    </article>
  );
};

export default AuthorityService;
